import debounce from 'debounce';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import i18n from 'helpers/i18n';
import messages from './messages';
import Masonry from 'react-masonry-css';
import PageLoader from 'components/PageLoader';
import TrainingCard from 'components/TrainingHistory/TrainingCard';
import { useEffect, useState } from 'react';
import { Search, Select } from '@teachstonellc/component-library';
import { Pagination } from 'components/Organization/Pagination';
import { useGetTrainingHistory } from 'actions/trainingHistory';
import { TrainingHistory } from 'types/api/TrainingHistory';
import { ClassIcon, filterOptions, sortOptions } from './utils';

const STATUS_DELAY = 1000;

const breakpointColumnsObj = {
  default: 3,
  768: 2,
  360: 1
};

type SearchParams = {
  query: string;
  type: string | null;
  sort_by: string;
  sort_dir: string;
  per_page: number;
  page: number;
};

export function TrainingList() {
  const [params, setParams] = useState<SearchParams>({
    query: '',
    type: null,
    sort_by: 'start_date',
    sort_dir: 'desc',
    per_page: 6,
    page: 1
  });
  const [pendingPageAnnouncement, setPendingPageAnnouncement] = useState<
    number | null
  >(null);
  const [pageChangeAnnouncement, setPageChangeAnnouncement] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const { data, isPending } = useGetTrainingHistory(params);

  const handleSortChange = (sortOption: string) => {
    let sort_by = 'start_date';
    let sort_dir = 'desc';

    switch (sortOption) {
      case 'asc':
        sort_by = 'start_date';
        sort_dir = 'asc';
        break;
      case 'a-z':
        sort_by = 'name';
        sort_dir = 'asc';
        break;
      case 'z-a':
        sort_by = 'name';
        sort_dir = 'desc';
        break;
      default:
        sort_by = 'start_date';
        sort_dir = 'desc';
        break;
    }

    setParams(prev => ({ ...prev, sort_by, sort_dir }));
  };

  function handleSearch(event: React.ChangeEvent<HTMLInputElement>) {
    setStatusMessage('');
    setParams(prev => ({
      ...prev,
      query: event.target.value,
      page: 1
    }));
  }

  const trainings = data?.training_histories ?? [];
  const anyTrainingCompleted = data?.any_training_completed ?? false;
  const totalPages = data?.pagination.total_pages || 1;

  // Announce the empty state only after the user searches or filters,
  // not on the initial page load.
  const hasActiveSearch = params.query !== '' || params.type !== null;
  const noResultsAnnouncement =
    !isPending && hasActiveSearch && trainings.length === 0
      ? anyTrainingCompleted
        ? i18n.ft(messages.noTrainingsTitle)
        : i18n.ft(messages.noTrainingsEverTitle)
      : '';

  // Wait a moment before updating the status region so the screen reader's
  // typed-character echo doesn't talk over it.
  useDebouncedEffect(
    () => setStatusMessage(noResultsAnnouncement),
    STATUS_DELAY,
    [noResultsAnnouncement, params.query, params.type]
  );

  useEffect(() => {
    if (pendingPageAnnouncement === null || isPending) {
      return;
    }

    setPageChangeAnnouncement(
      i18n.ft(messages.paginationPageChangeAnnouncement, {
        page: pendingPageAnnouncement,
        total: totalPages,
        count: trainings.length
      })
    );
    setPendingPageAnnouncement(null);
  }, [isPending, pendingPageAnnouncement, totalPages, trainings.length]);

  return (
    <section>
      <h2 className="font-sans font-bold text-white text-2xl bg-orange-500 px-6 py-3 rounded-t-xl">
        {i18n.ft(messages.trainingCourses)}
      </h2>

      <div className="mt-6">
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {pageChangeAnnouncement}
        </div>

        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {statusMessage}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-[2fr_minmax(320px,1fr)_minmax(320px,1fr)] gap-4 items-end">
          <Search
            id="search"
            label={i18n.ft(messages.search)}
            placeholder={i18n.ft(messages.searchPlaceholder)}
            onChange={debounce(handleSearch, 300)}
          />

          <Select
            label={i18n.ft(messages.filterByType)}
            value={params.type}
            onChange={value => {
              if (typeof value === 'string' || value === null) {
                setStatusMessage('');
                setParams(prev => ({ ...prev, type: value }));
              }
            }}
            options={filterOptions}
          />

          <Select
            label={i18n.ft(messages.sortBy)}
            value={
              params.sort_by === 'name'
                ? params.sort_dir === 'asc'
                  ? 'a-z'
                  : 'z-a'
                : params.sort_dir
            }
            onChange={value => {
              if (typeof value === 'string') {
                handleSortChange(value);
              }
            }}
            options={sortOptions}
          />
        </div>

        {isPending ? (
          <PageLoader />
        ) : (
          <TrainingListView
            trainings={trainings}
            anyTrainingCompleted={anyTrainingCompleted}
          />
        )}
      </div>

      {trainings?.length > 0 && (
        <div className="mt-6 flex justify-center">
          <Pagination
            page={params.page}
            onPageChange={newPage => {
              setPendingPageAnnouncement(newPage);
              setPageChangeAnnouncement('');
              setParams(prev => ({ ...prev, page: newPage }));
            }}
            total={data?.pagination.total_pages || 1}
            ariaLabel={i18n.ft(messages.paginationAriaLabel)}
            goTo={false}
          />
        </div>
      )}
    </section>
  );
}

interface TrainingListViewProps {
  trainings: TrainingHistory[];
  anyTrainingCompleted: boolean;
}

function TrainingListView({
  trainings,
  anyTrainingCompleted
}: TrainingListViewProps) {
  return (
    <>
      <hr className="my-8 border-navy-100" />

      {trainings.length === 0 && (
        <div className="pt-8 pb-16 px-12 bg-white shadow-sm rounded-3xl flex flex-col items-center">
          <ClassIcon />

          <div className="w-1/2 mt-8 text-xl font-bold text-center">
            {anyTrainingCompleted
              ? i18n.ft(messages.noTrainingsTitle)
              : i18n.ft(messages.noTrainingsEverTitle)}
          </div>

          <div className="w-1/2 mt-3 text-base text-center">
            {i18n.ft(messages.noTrainingsContent)}
          </div>
        </div>
      )}

      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="flex -ml-[15px] w-auto"
        columnClassName="pl-[15px] bg-clip-padding"
      >
        {trainings.map(training => (
          <TrainingCard key={training.id} training={training} />
        ))}
      </Masonry>
    </>
  );
}
